import { useState } from 'react';
import {
  BookOpenText,
  CaretRight,
  Database,
  MagnifyingGlass,
  SpinnerGap,
  Table as TableIcon,
  WarningCircle,
  Check,
  Brain,
  ChatText,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { AssistantToolEvent } from '@/pages/assistant/conversations';

const TOOL_META: Record<string, { icon: typeof Database; label: string }> = {
  search_spec: { icon: MagnifyingGlass, label: 'Searched spec' },
  grep_spec: { icon: MagnifyingGlass, label: 'Searched spec (exact)' },
  read_spec: { icon: BookOpenText, label: 'Read spec' },
  get_table_schema: { icon: TableIcon, label: 'Checked schema' },
  run_sql: { icon: Database, label: 'Ran SQL' },
};

function toolInputPreview(event: AssistantToolEvent): string {
  try {
    const parsed = JSON.parse(event.input) as Record<string, unknown>;
    if (event.name === 'run_sql') return String(parsed.sql ?? event.input);
    if (event.name === 'search_spec') return `"${String(parsed.query ?? '')}"`;
    if (event.name === 'grep_spec') return `"${String(parsed.pattern ?? '')}"`;
    if (event.name === 'read_spec') return String(parsed.id ?? '');
    if (event.name === 'get_table_schema') return String(parsed.table ?? '');
    return event.input;
  } catch {
    return event.input;
  }
}

function NoteRow({ event }: { event: AssistantToolEvent }) {
  return (
    <div className="flex items-start gap-1.5 px-1.5 py-1 text-[11px] italic text-muted-foreground/80">
      <ChatText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{event.input}</span>
    </div>
  );
}

function ToolRow({ event }: { event: AssistantToolEvent }) {
  const [open, setOpen] = useState(false);
  const meta = TOOL_META[event.name] ?? { icon: Database, label: event.name };
  const Icon = meta.icon;
  const preview = toolInputPreview(event);
  const isSql = event.name === 'run_sql';

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-1.5 rounded-md px-1.5 py-1 text-left hover:bg-muted/60"
      >
        <CaretRight
          className={cn(
            'mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform',
            open && 'rotate-90',
          )}
        />
        {event.status === 'running' ? (
          <SpinnerGap className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
        ) : event.status === 'error' ? (
          <WarningCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-error" />
        ) : (
          <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1">
          <span className="font-medium text-foreground/80">{meta.label}</span>{' '}
          <span
            className={cn(
              'text-muted-foreground',
              !open && 'line-clamp-1 break-all',
              isSql && 'font-mono text-[11px]',
            )}
          >
            {open ? '' : preview}
          </span>
        </span>
        {event.durationMs !== undefined && (
          <span className="ml-auto shrink-0 pl-2 tabular-nums text-muted-foreground/70">
            {event.durationMs >= 1000
              ? `${(event.durationMs / 1000).toFixed(1)}s`
              : `${event.durationMs}ms`}
          </span>
        )}
      </button>
      {open && (
        <div className="ml-6 mt-0.5 space-y-1 pb-1">
          <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md border bg-muted/40 p-2 font-mono text-[11px] leading-snug">
            {preview}
          </pre>
          {event.summary && (
            <div
              className={cn(
                'px-1 text-[11px]',
                event.status === 'error'
                  ? 'text-error'
                  : 'text-muted-foreground',
              )}
            >
              → {event.summary}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The assistant's live activity trail: thinking summary + every tool call
 * with status and timing. Expanded while streaming, collapsible afterwards.
 */
export function ToolTrail({
  toolEvents,
  thinking,
  streaming,
}: {
  toolEvents: AssistantToolEvent[];
  thinking?: string;
  streaming: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const hasAnything = toolEvents.length > 0 || (thinking?.length ?? 0) > 0;
  if (!hasAnything) return null;

  const sqlCount = toolEvents.filter((e) => e.name === 'run_sql').length;
  const specCount = toolEvents.filter(
    (e) =>
      e.name === 'search_spec' ||
      e.name === 'grep_spec' ||
      e.name === 'read_spec',
  ).length;
  const failures = toolEvents.filter((e) => e.status === 'error').length;
  const summaryBits = [
    specCount > 0 && `${specCount} spec lookup${specCount > 1 ? 's' : ''}`,
    sqlCount > 0 && `${sqlCount} quer${sqlCount > 1 ? 'ies' : 'y'}`,
    failures > 0 && `${failures} failed`,
  ]
    .filter(Boolean)
    .join(' · ');

  const showBody = streaming || !collapsed;

  return (
    <div className="mb-2 overflow-hidden rounded-lg border border-border/70 bg-card/60">
      <button
        type="button"
        onClick={() => !streaming && setCollapsed((v) => !v)}
        className={cn(
          'flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] font-medium text-muted-foreground',
          !streaming && 'hover:bg-muted/50',
        )}
      >
        {streaming ? (
          <SpinnerGap className="h-3.5 w-3.5 animate-spin text-primary" />
        ) : failures > 0 ? (
          <WarningCircle className="h-3.5 w-3.5 text-warning" />
        ) : (
          <Check className="h-3.5 w-3.5 text-success" />
        )}
        <span>
          {streaming ? 'Working' : 'Worked'}
          {summaryBits ? ` — ${summaryBits}` : '…'}
        </span>
        {!streaming && (
          <CaretRight
            className={cn(
              'ml-auto h-3 w-3 transition-transform',
              showBody && 'rotate-90',
            )}
          />
        )}
      </button>
      {showBody && (
        <div className="space-y-0.5 border-t border-border/60 px-1 py-1">
          {thinking && (
            <div className="flex items-start gap-1.5 px-1.5 py-1 text-[11px] italic text-muted-foreground/80">
              <Brain className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span
                className={cn(streaming && 'animate-pulse', 'line-clamp-3')}
              >
                {thinking.length > 600 ? `…${thinking.slice(-600)}` : thinking}
              </span>
            </div>
          )}
          {toolEvents.map((event) =>
            event.name === 'note' ? (
              <NoteRow key={event.id} event={event} />
            ) : (
              <ToolRow key={event.id} event={event} />
            ),
          )}
        </div>
      )}
    </div>
  );
}
